import { useState, useEffect } from 'react';
import { useToast } from '../../Utils/toastContext';
import { useNavigate } from 'react-router-dom';

const CountryPercentageField = ({ value = [], onChange, disabled }) => {
  const [rows, setRows] = useState(value);
  const [countries, setCountries] = useState([]);
  const [searchMap, setSearchMap] = useState({}); // per-row search
  const [openIndex, setOpenIndex] = useState(null);
  const navigate = useNavigate();

  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { showToast } = useToast();
  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };
  const getEddResponses = async () => {
    try {
      const response = await GetAPIcalls(`/api/v1/users/countries?size=1000`);
      if (response.status == 200) {
        setCountries(response.data.countries);
      }
    } catch (err) {
      setCountries([]);
      showToast(err.message, 'error');
    }
  };

  useEffect(() => {
    getEddResponses();
  }, []);

  // Sync with parent
  useEffect(() => {
    setRows(value || []);
  }, [value]);

  const emitChange = (updated) => {
    setRows(updated);
    onChange?.(updated);
  };

  const addRow = () => {
    const updated = [...rows, { option: { id: null, label: '' }, percent: '' }];
    emitChange(updated);
  };

  const removeRow = (index) => {
    const updated = rows.filter((_, i) => i !== index);
    emitChange(updated);
  };

  const updatePercent = (index, value) => {
    const updated = [...rows];
    updated[index].percent = value;
    emitChange(updated);
  };

  const updateCountry = (index, country) => {
    const updated = [...rows];
    updated[index].option = {
      id: country.id,
      label: country.label || country.name,
    };
    setOpenIndex(null);
    emitChange(updated);
  };

  return (
    <div className="space-y-3">
      {rows.map((row, index) => {
        const search = searchMap[index] || '';

        const filteredCountries =
          search.length > 0
            ? countries.filter((c) =>
                (c.label || c.name || '')
                  .toLowerCase()
                  .includes(search.toLowerCase()),
              )
            : [];

        return (
          <div key={index} className="flex gap-2 items-center relative text-sm">
            {/* COUNTRY INPUT */}
            <div className="relative w-2/3">
              <input
                type="text"
                value={row.option?.label || ''}
                disabled={disabled}
                placeholder="Start typing country..."
                className="w-full border rounded px-3 py-2"
                onFocus={() => setOpenIndex(index)}
                onChange={(e) => {
                  setSearchMap({
                    ...searchMap,
                    [index]: e.target.value,
                  });

                  const updated = [...rows];
                  updated[index].option = {
                    id: null,
                    label: e.target.value,
                  };
                  emitChange(updated);

                  setOpenIndex(index);
                }}
              />

              {/* DROPDOWN */}
              {openIndex === index && search && (
                <div className="absolute bg-white border w-full max-h-40 overflow-y-auto z-20 shadow rounded">
                  {filteredCountries.map((c) => (
                    <div
                      key={c.id}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() =>
                        updateCountry(index, {
                          id: c.id,
                          label: c.label || c.name,
                        })
                      }
                    >
                      {c.label || c.name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* PERCENT */}
            <input
              type="number"
              value={row.percent || ''}
              disabled={disabled}
              placeholder="%"
              className="w-24 border rounded px-3 py-2 text-right"
              onChange={(e) => updatePercent(index, e.target.value)}
            />

            {/* REMOVE */}
            <button
              onClick={(e) => {
                e.preventDefault();
                removeRow(index);
              }}
              disabled={disabled}
              className="text-red-500"
            >
              ✕
            </button>
          </div>
        );
      })}

      {/* ADD ROW */}
      <button
        onClick={(e) => {
          e.preventDefault();
          addRow();
        }}
        disabled={disabled}
        className="text-blue-600 text-sm"
      >
        + Add Country
      </button>
    </div>
  );
};

export default CountryPercentageField;
